import React, { useState, useRef, useEffect } from "react";
import {
  Mic,
  StopCircle,
  Upload,
  Loader2,
  Check,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config";

const ListeningTest: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("idle");
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadResponse, setUploadResponse] = useState<any>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const navigate = useNavigate();
  const { questionId } = useParams<{ questionId: string }>();

  // Validate that questionId exists
  useEffect(() => {
    if (!questionId) {
      console.error("No questionId found in URL");
      // You could redirect to an error page or show an error message
      return;
    }
    console.log("Question ID extracted from URL:", questionId);
  }, [questionId]);

  // Handle recording start/stop
  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        setStatus("processing");
        if (timerRef.current) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    } else {
      // Start recording
      audioChunksRef.current = [];
      setStatus("idle");
      setRecordingTime(0);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: "audio/wav",
          });
          const audioUrl = URL.createObjectURL(audioBlob);

          setRecordedAudio(audioBlob);
          setAudioURL(audioUrl);
          setStatus("recorded");

          // Stop all tracks on the stream to release the microphone
          stream.getTracks().forEach((track) => track.stop());
        };

        mediaRecorder.start();
        setIsRecording(true);
        setStatus("recording");

        // Start timer for recording duration
        timerRef.current = window.setInterval(() => {
          setRecordingTime((prev) => prev + 1);
        }, 1000);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        setStatus("error");
      }
    }
  };

  // Reset recording state and allow for retake
  const handleRetake = () => {
    if (isRecording && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();

      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }

      setIsRecording(false);
    }

    // Clean up the existing audio URL
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }

    // Reset state
    setRecordedAudio(null);
    setAudioURL(null);
    setStatus("idle");
    setRecordingTime(0);
  };

  // Upload recorded audio to server
  const uploadToServer = async () => {
    if (!recordedAudio) return;

    setUploading(true);
    setStatus("uploading");

    try {
      const formData = new FormData();
      const fileName = `recording-1-${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-")}.wav`;
      formData.append("audio", recordedAudio, fileName);

      const uploadResponse = await fetch(
        `${API_BASE_URL}/upload/audio`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      const uploadData = await uploadResponse.json();
      setUploadResponse(uploadData);
      setStatus("success");
    } catch (error) {
      console.error("Error uploading audio:", error);
      setStatus("uploadError");
    } finally {
      setUploading(false);
    }
  };

  // Format recording time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Clean up URLs when component unmounts
  useEffect(() => {
    return () => {
      if (audioURL) {
        URL.revokeObjectURL(audioURL);
      }
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, [audioURL]);

  // Status icon based on current state
  const StatusIcon = () => {
    switch (status) {
      case "recording":
        return <Mic size={18} className="text-red-500 animate-pulse" />;
      case "processing":
        return <Loader2 size={18} className="text-amber-500 animate-spin" />;
      case "recorded":
        return <Check size={18} className="text-green-500" />;
      case "uploading":
        return <Loader2 size={18} className="text-blue-500 animate-spin" />;
      case "success":
        return <Check size={18} className="text-green-500" />;
      case "error":
        return <AlertCircle size={18} className="text-red-500" />;
      case "uploadError":
        return <AlertCircle size={18} className="text-red-500" />;
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    try {
      if (!questionId) {
        console.error("No questionId available");
        return;
      }
      const allAnswers = [
        {
          answer: uploadResponse.audioUrl,
          marks: 0,
        },
      ];

      const updateAnswerResponse = await fetch(
        `${API_BASE_URL}/screening-questions/${questionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            listening: allAnswers,
          }),
        }
      );

      if (!updateAnswerResponse.ok) {
        throw new Error(
          `Answer update failed with status: ${updateAnswerResponse.status}`
        );
      }
      navigate("/thank-you");
    } catch (error) {
      console.error("Error submitting:", error);
    }
  };

  // Add this useEffect at the top of your other useEffects
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const hasUploadedAudio = uploadResponse !== null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-200 justify-center items-center px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <div className="bg-white p-8 rounded-3xl shadow-lg mb-6 overflow-hidden relative">
          <div className="bg-red-50 h-40 rounded-full w-40 -mr-12 -mt-12 absolute opacity-70 right-0 top-0"></div>
          <div className="bg-red-50 h-32 rounded-full w-32 -mb-8 -ml-8 absolute bottom-0 left-0 opacity-70"></div>
          <div className="relative z-10">
            <h1 className="bg-clip-text bg-gradient-to-r text-4xl text-transparent font-bold from-red-600 mb-5 to-pink-600">
              Listening Assessment
            </h1>
            <div className="bg-gradient-to-r h-1.5 rounded-full w-24 from-red-500 mb-6 to-pink-500"></div>
          </div>
        </div>

        <div className="flex flex-col items-center p-6 max-w-4xl mx-auto rounded-3xl shadow-lg bg-white">
          <div className="w-full text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-800">Voice Recorder</h2>
            <p className="text-red-600 mt-1">Record and send your audio</p>
          </div>

          {/* Visualizer/Status display */}
          <div className="w-full bg-white bg-opacity-60 rounded-lg p-4 mb-6 shadow-inner">
            <div className="flex items-center gap-2 mb-2">
              <StatusIcon />
              <span
                className={`text-sm font-medium ${
                  status === "error" || status === "uploadError"
                    ? "text-red-600"
                    : "text-gray-700"
                }`}
              >
                {status === "recording"
                  ? `Recording (${formatTime(recordingTime)})`
                  : status === "processing"
                  ? "Processing audio..."
                  : status === "recorded"
                  ? "Audio recorded successfully!"
                  : status === "uploading"
                  ? "Uploading to server..."
                  : status === "success"
                  ? "Upload complete!"
                  : status === "error"
                  ? "Error accessing microphone"
                  : status === "uploadError"
                  ? "Error uploading audio"
                  : "Ready to record"}
              </span>
            </div>

            {/* Audio visualization (simplified) */}
            {isRecording && (
              <div className="flex items-center justify-center h-12 gap-1">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-red-500 w-1 rounded-full animate-pulse"
                    style={{
                      height: `${Math.random() * 100}%`,
                      animationDelay: `${i * 0.05}s`,
                    }}
                  ></div>
                ))}
              </div>
            )}
          </div>

          {/* Recording button - hide when audio is recorded */}
          {!recordedAudio && (
            <button
              onClick={toggleRecording}
              disabled={uploading}
              className={`relative flex items-center justify-center p-5 rounded-full mb-6 shadow-lg transition-all transform hover:scale-105 ${
                isRecording
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600"
              } text-white disabled:opacity-50`}
            >
              {isRecording ? <StopCircle size={32} /> : <Mic size={32} />}
            </button>
          )}

          {/* Audio player with retake option */}
          {audioURL && (
            <div className="w-full mb-6">
              <div className="bg-white bg-opacity-60 p-3 rounded-lg shadow-inner mb-3">
                <audio src={audioURL} controls className="w-full" />
              </div>

              {/* Action buttons container */}
              <div className="flex gap-3">
                {/* Retake button */}
                <button
                  onClick={handleRetake}
                  disabled={uploading}
                  className="flex-1 p-3 bg-white border border-red-500 text-red-600 hover:bg-red-50 rounded-lg flex items-center justify-center gap-2 shadow transition-all hover:shadow-md disabled:opacity-50"
                >
                  <RefreshCw size={18} />
                  <span>Retake</span>
                </button>

                {/* Upload button */}
                <button
                  onClick={uploadToServer}
                  disabled={uploading || status === "success"}
                  className="flex-1 p-3 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white rounded-lg flex items-center justify-center gap-2 shadow-lg transition-all hover:shadow-xl disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : status === "success" ? (
                    <>
                      <Check size={18} />
                      <span>Uploaded</span>
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      <span>Upload</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Submit button - only show after successful upload */}
          {hasUploadedAudio && (
            <div className="w-full mt-6">
              <button
                onClick={handleSubmit}
                className="w-full p-4 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg transition-all hover:shadow-xl"
              >
                <Check size={18} />
                <span>Submit</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListeningTest;
